import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/Button";
import { grey } from "@mui/material/colors";
import { useDrop } from "react-dnd";
import { useSnackbar } from "notistack";
import useActions, { useAppDispatch, useAppSelector } from "../../app/hooks";
//import { ChartEditorHeaderMenu } from "./header/EditorHeaderMenu";
import { ChartEditor } from "./document/ChartEditor";
import { ChartAdder } from "./document/ChartAdder";
import BackgroundTasksPanel from "../ide/BackgroundTasksPanel";
import IdeHistoryPanel from "../ide/IdeHistoryPanel";

import {
  selectCodeComments,
  selectCodeProposals,
  selectCodeSelection,
  selectEditingDash,
  selectOverrideCode,
  selectPageNo,
  selectSelected,
  selectViewCodeState,
  setCodeSelection,
  setFileDataChanged,
  setOverrideCode,
  setViewCodeState,

} from "./dashEditorSlice";
import { selectAuth, selectCurrentWorkspaceRole } from "../auth/authSlice";
import { Chart, Dash } from "../../types/Dash";
import { selectFocusChart, selectLibrary, setError, openFuncMap } from "../library/librarySlice";
import { setIdeLocation } from "../ide/ideSlice";
//import { clearPreview, selectDocumentPreview } from "../library/librarySlice";
import PreviewModal from "../preview/PreviewModal";
import { selectPreferences } from "./header/preferencesSlice";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Chip from "@mui/material/Chip";
import { AddCircleOutline } from "@mui/icons-material";

import CodeMirror from '@uiw/react-codemirror';
//import { rust } from '@codemirror/lang-rust';
import { PlanProcessing } from "./planprocessing/PlanProcessing";
import { ShowRelationships } from "./planprocessing/ShowRelationships";
import HexEditor from "./HexEditor";
import { codeCommentHoverTooltipExtension, lineHighlightExtension } from "./codeCommentLineHighlight";
import { rubyKeywordHighlightExtension } from "./tools/ruby_highlighting/rubyHighlightExtension";
import { languageExtensionsForFile } from "./tools/languageForFile";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

/**
 * The main document editor. When a DocumentTemplate is set
 * this is created to allow it to be edited.
 *
 * @export
 * @return {*}
 */
export default function DashEditor(props: {
  from_outside: boolean,
}) {
  const actions = useActions();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const dash = useAppSelector(selectEditingDash);
  const pageNo = useAppSelector(selectPageNo);

  const overrideCode = useAppSelector(selectOverrideCode);
  const librarySlice = useAppSelector(selectLibrary);

  const viewCodeState = useAppSelector(selectViewCodeState);
  const codeSelection = useAppSelector(selectCodeSelection);
  const auth = useAppSelector(selectAuth);
  const codeProposals = useAppSelector(selectCodeProposals);
  const codeComments = useAppSelector(selectCodeComments);

  const focusChart = useAppSelector(selectFocusChart);

  const providesCodeAutomations = (librarySlice.automations || []).filter(
    (a) => a.provides_code
  );
  const showAutomationsPanel =
    viewCodeState === 3 &&
    !!codeSelection?.trim() &&
    providesCodeAutomations.length > 0;

  const [currentCode, setCurrentCode] = useState(dash.current_code === null ? "" : dash.current_code);
  const [currentCodeChange, setCurrentCodeChange] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [proceedOpen, setProceedOpen] = useState(false);
  const [displayOpen, setDisplayOpen] = useState(false);
  const [hexEditorOpen, setHexEditorOpen] = useState(false);

  const differentRepo = useEffect(() => {
    if (dash.id !== null && dash.id !== undefined && dash.id > 0) {
      setCurrentCode(dash.current_code === null ? "" : dash.current_code);
    }
  }, [dash.id]);

  const override = useEffect(() => {
    if (overrideCode !== null) {
      setCurrentCode(overrideCode!);
      dispatch(setOverrideCode({overrideCode:null}));
    }
  }, [overrideCode])

  const parameters = dash.parameters !== null && dash.parameters !== undefined ? dash.parameters : [];

  const [parameterValues, setParameterValues] = useState(() => {
    const result = new Map<string, string>();
    
    parameters.forEach(_ => {
      result.set(_.name, _.default_value);
    });

    return result;
  });

  const viewPreferences = useAppSelector(selectPreferences);
  
  //const documentPreview = useAppSelector(selectDocumentPreview);
  const selectedComponents = useAppSelector(selectSelected);

  const currentWorkspaceRole = useAppSelector(selectCurrentWorkspaceRole);

  const isEditor = currentWorkspaceRole == "ADMIN";

  const chartRefs: { [id: number] : HTMLDivElement; } = {};
  const isRubyFile =
    (dash.repo_file_name && dash.repo_file_name.endsWith(".rb")) ||
    (dash.name && dash.name.endsWith(".rb"));
  // C / C++ / Objective-C(++) / headers — picked by file extension.
  const codeLanguageExtensions = languageExtensionsForFile(dash.repo_file_name ?? dash.name);

  function handleCurrentCodeChange(value:string) {
    setCurrentCode(value);
  }

  function handleProceedClick() {
    setProceedOpen(true);
  }

  const codeChangeEffect = useEffect( () => {
    if (dash.current_code != currentCode) {
      setCurrentCodeChange(true);

      //dispatch(setFileDataChanged(true));
    }
    else {
      setCurrentCodeChange(false);

      //dispatch(setFileDataChanged(false));
    }
  }, [currentCode, dash.current_code]);

  // Store IDE location when dash is associated with a repo/file
  useEffect(() => {
    if (dash && (dash.repo_id !== null || dash.repo_file_name !== null)) {
      const repoId = dash.repo_id !== null ? dash.repo_id : -1;
      const repoFileName = dash.repo_file_name || "";
      const lastSlash = repoFileName.lastIndexOf("/");
      const folderPath = lastSlash > 0 ? repoFileName.substring(0, lastSlash) : "/";

      dispatch(
        setIdeLocation({
          repoId,
          dashId: dash.id,
          repoFileName,
          folderPath,
        })
      );

      // Persist IDE state to backend
      actions.saveIdeState();
    }
  }, [dash.id, dash.repo_id, dash.repo_file_name, dispatch]);

  // Clear any stored code selection when leaving CURRENT CODE view
  useEffect(() => {
    if (viewCodeState !== 3) {
      dispatch(setCodeSelection({ codeSelection: null }));
    }
  }, [viewCodeState, dispatch]);

  // Clear selection when hex editor is opened (no CodeMirror selection available)
  useEffect(() => {
    if (hexEditorOpen) {
      dispatch(setCodeSelection({ codeSelection: null }));
    }
  }, [hexEditorOpen, dispatch]);

  // Fetch code proposals when entering PROPOSED CODE view
  useEffect(() => {
    if (viewCodeState === 2 && dash && dash.id) {
      actions.getCodeProposals({ dashId: dash.id });
    }
  }, [viewCodeState, dash?.id]);

  // Fetch code comments when entering CURRENT CODE view
  useEffect(() => {
    if (viewCodeState === 3 && dash && dash.id) {
      actions.getCodeComments({ dashId: dash.id });
    }
  }, [viewCodeState, dash?.id]);

  // Fetch automations when in CURRENT CODE view so provides_code list can show on selection
  useEffect(() => {
    if (viewCodeState === 3) {
      actions.getAutomations();
    }
  }, [viewCodeState]);

  // Show toast notification when save completes
  useEffect(() => {
    if (isSaving) {
      if (librarySlice.status === "idle") {
        setIsSaving(false);
        if (librarySlice.error && librarySlice.error === "File saved!") {
          enqueueSnackbar("File saved successfully", { variant: "success" });
          dispatch(setError(undefined));
        } else if (librarySlice.error && librarySlice.error !== "File saved!") {
          enqueueSnackbar(`Save failed: ${librarySlice.error}`, { variant: "error" });
          dispatch(setError(undefined));
        }
      } else if (librarySlice.status === "failed" || (librarySlice.status === "loading" && librarySlice.error && librarySlice.error !== "File saved!")) {
        // Handle error case - status might be "failed" or still "loading" with an error
        setIsSaving(false);
        enqueueSnackbar(`Save failed: ${librarySlice.error}`, { variant: "error" });
        dispatch(setError(undefined));
      }
    }
  }, [librarySlice.status, librarySlice.error, isSaving, enqueueSnackbar, dispatch]);



  const scrollEffect = useEffect( () => {
    if (focusChart !== null && chartRefs[focusChart.id] !== null && chartRefs[focusChart.id] !== undefined) {
      chartRefs[focusChart.id].scrollIntoView();
    }
  }, [focusChart]);

  function handleAddNavigationClicked(e: any) {
    // Get a navigation name
    const name = window.prompt("Name of navigation?");

    // Figure out the page numbers
    let pageNo = 1;
    let maxPageNo = 0;
    dash.navigations?.forEach(_ => {
      if (_.page_no > maxPageNo) {
        maxPageNo = _.page_no;
      }
    });

    if (maxPageNo > 0) {
      pageNo = maxPageNo + 1;
    }

    if (name !== null && name !== undefined) {


      actions.createNavigation({
        parent_id: -1,
        name: name,
        page_no: pageNo,
        valid_roles: ""
      });
    }
  }

  function handleSetPageClicked(e: any, pageNo:number) {
    actions.setPageNo(pageNo);
  }

  /*
  function handleDeleteNavigationClicked(e: any, id:number) {

  }

  function handleEditNavigationClicked(e: any, id:number) {
    
  }*/

  const [pageRef, setPageRef] = React.useState<null | HTMLDivElement>(null);

  return (
    <Box>
      <AppBar></AppBar>
      <Box sx={{ px: 2 }}>
         {/* <DashHeaderMenu
          organizationId={auth.user!.default_organization_id!}
          workspaceId={auth.user!.default_workspace_id!}
          dash={dash}
  ></DashHeaderMenu> */}
      </Box>

      {/* Big page view */}
      <Box
        sx={{
          bgcolor: grey[400],
          px: 5,
          display: "flex",
          flexDirection: "column",
          height: "87vh",
          overflow: "hidden",
          overflowY: "scroll",
          scrollBehavior: "smooth",
        }}
        ref={ (ref:HTMLDivElement) => setPageRef(ref) }
      >
        {/* Centered box for page  ref={ drop } */}
        <Box
          
          sx={{
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
          }}
        >
          {/* The white board */}
          <Paper elevation={3} sx={{ my: 1, width: "850px" }}>
            {/* The sections, sorted already by component_order */}

            {/* Navigation block atop a dashboard */}
            { dash !== null &&
              <Box sx={{ padding: "10px"}}>
                
                { !props.from_outside && <Typography sx={{marginBottom: "10px" }}><strong>WORKBOOK:</strong> { dash.name } #{dash.id}</Typography> }
                { props.from_outside && <Typography sx={{marginBottom: "10px", fontSize: "1.3em", fontWeight: "bold" }}>{ dash.name }</Typography> }
                <Box>
                  {
                    dash.navigations?.map(_ => (
                      <Button key={"nav_" + _.id}variant={_.page_no == pageNo ? "contained" : "outlined"} sx={{marginRight: "5px" }}
                        onClick={(e) => handleSetPageClicked(e, _.page_no)}>
                        { _.name }
                      </Button>
                    ))
                  }
                  { !props.from_outside && isEditor && <IconButton
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleAddNavigationClicked}
                  >
                    <AddCircleOutline/>
                  </IconButton> }
                  { dash.repo_id !== null && <Box>
                    <Button key={"but_workbook_code"} variant={viewCodeState == 1 ? "contained" : "outlined"} sx={{marginRight: "5px" }}
                      onClick={(e) => dispatch(setViewCodeState(1))}>
                      WORKBOOK
                    </Button>
                    <Button key={"but_proposed_code"} variant={viewCodeState == 2 ? "contained" : "outlined"} sx={{marginRight: "5px" }}
                      onClick={(e) => dispatch(setViewCodeState(2))}>
                      PROPOSED CODE
                    </Button>
                    <Button key={"but_current_code"} variant={viewCodeState == 3 ? "contained" : "outlined"} sx={{marginRight: "5px" }}
                      onClick={(e) => dispatch(setViewCodeState(3))}>
                      CURRENT CODE
                    </Button>
                    { viewCodeState == 3 && (
                      <>
                        <Button
                          key={"but_hex_editor"}
                          variant={hexEditorOpen ? "contained" : "outlined"}
                          sx={{marginRight: "5px" }}
                          onClick={() => setHexEditorOpen((v) => !v)}
                        >
                          HEX EDITOR
                        </Button>
                        <Button
                          key={"but_view_mapping"}
                          variant="outlined"
                          sx={{marginRight: "5px" }}
                          disabled={!dash.repo_file_name}
                          onClick={() => {
                            if (dash.repo_file_name) {
                              const repoId = dash.repo_id ?? -1;
                              dispatch(openFuncMap({ filePath: dash.repo_file_name, repoId }));
                              actions.mapFile({ repoId, filePath: dash.repo_file_name });
                            }
                          }}
                        >
                          VIEW MAPPING
                        </Button>
                        <Button
                          key={"but_update_current_code"}
                          variant="contained"
                          onClick={(e) => {
                            setIsSaving(true);
                            actions.updateDashFile({ dashId: dash.id, currentCode: currentCode});
                          }}
                          style={{ backgroundColor: currentCodeChange ? "#DD0000" : "#000000", marginRight: "5px"}}
                        >
                          SAVE
                        </Button>
                      </>
                    )}
                    {dash.name && dash.name.endsWith(".vgrf") && (
                      <Button
                        key={"but_display_vgrf"}
                        variant="outlined"
                        sx={{ ml: 1 }}
                        onClick={() => setDisplayOpen(true)}
                      >
                        DISPLAY
                      </Button>
                    )}
                    {dash.name && dash.name.endsWith(".vgpf") && (
                      <Button
                        key={"but_proceed_vgpf"}
                        variant="outlined"
                        sx={{ ml: 1 }}
                        onClick={handleProceedClick}
                      >
                        PROCEED
                      </Button>
                    )}
                  </Box> }
                </Box>
              </Box> }
            { viewCodeState == 1 && <Box>
              { dash !== null ? dash!.charts!.filter(_ => _.page_no == pageNo).map(
                (chart: Chart, index) => (
                  <ChartEditor
                    from_outside={props.from_outside}
                    key={chart.id}
                    organizationId={auth.user!.default_organization_id!}
                    workspaceId={auth.user!.default_workspace_id!}
                    dash={dash}
                    chart={chart}
                    viewPreferences={viewPreferences}
                    isSelected={selectedComponents.chart?.id === chart.id}
                    is_editor={isEditor}
                    set_ref={ (ref:HTMLDivElement) => chartRefs[chart.id] = ref }
                    set_parm_value={ (parm:string, value:string) => {
                      const clone = parameterValues;
                      clone.set(parm, value);
                      setParameterValues(clone);
                      }}
                    parameters={parameters}
                    parameter_values={parameterValues}
                  />
                )
              ) : ""}
              { /*isOver ? (
                <Box>
                  <Typography variant="h1">{<Skeleton />}</Typography>
                  <Typography variant="h3">{<Skeleton />}</Typography>
                  <Typography variant="h3">{<Skeleton />}</Typography>
                </Box>
              ) : null */}

              { isEditor && <ChartAdder
                organizationId={auth.user!.default_organization_id!}
                workspaceId={auth.user!.default_workspace_id!}
                dash={dash}
                pageNo={pageNo}
              ></ChartAdder> }
            </Box> }

            { viewCodeState == 2 && 
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Code Proposals
                </Typography>
                {codeProposals.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No code proposals available.
                  </Typography>
                ) : (
                  <List>
                    {codeProposals.map((proposal) => (
                      <ListItem
                        key={proposal.id || `${proposal.chart_id}_${proposal.created_at}`}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          mb: 1,
                          flexDirection: "column",
                          alignItems: "stretch",
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                          <Box>
                            <Typography variant="subtitle2">
                              Proposal ID: {proposal.id} | Chart ID: {proposal.chart_id}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {proposal.accepted && (
                              <Chip label="Accepted" color="success" size="small" />
                            )}
                            {proposal.closed && (
                              <Chip label="Closed" color="default" size="small" />
                            )}
                            {proposal.code_proposal && !proposal.closed && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  if (dash && proposal.code_proposal) {
                                    actions.assignCodeProposal({
                                      dashId: dash.id,
                                      proposalId: proposal.id,
                                      code: proposal.code_proposal,
                                    });
                                  }
                                }}
                              >
                                Assign
                              </Button>
                            )}
                          </Box>
                        </Box>
                        {proposal.code_proposal && (
                          <Box
                            component="pre"
                            sx={{
                              m: 0,
                              p: 1,
                              bgcolor: "grey.100",
                              borderRadius: 1,
                              fontFamily: "monospace",
                              fontSize: "0.8rem",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              maxHeight: 200,
                              overflow: "auto",
                            }}
                          >
                            {proposal.code_proposal}
                          </Box>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          Created: {new Date(proposal.created_at).toLocaleString()}
                          {proposal.updated_at !== proposal.created_at && (
                            <> | Updated: {new Date(proposal.updated_at).toLocaleString()}</>
                          )}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>}

            { viewCodeState == 3 && 
              <Box>
                { hexEditorOpen ? (
                  <HexEditor
                    value={currentCode}
                    onChange={setCurrentCode}
                    onClose={() => setHexEditorOpen(false)}
                  />
                ) : (
                  <>
                    <CodeMirror
                      value={currentCode}
                      height="70vh"
                      onChange={handleCurrentCodeChange}
                      onUpdate={(viewUpdate) => {
                        const state = viewUpdate.state;
                        const sel = state.selection.main;
                        if (!sel || sel.empty) {
                          dispatch(setCodeSelection({ codeSelection: null }));
                        } else {
                          const text = state.doc.sliceString(sel.from, sel.to);
                          dispatch(setCodeSelection({ codeSelection: text }));
                        }
                      }}
                      extensions={
                        [
                          ...codeLanguageExtensions,
                          ...(codeComments.length > 0
                            ? [
                                lineHighlightExtension(codeComments.map((c) => c.line_number)),
                                codeCommentHoverTooltipExtension(codeComments),
                              ]
                            : []),
                          ...(isRubyFile ? [rubyKeywordHighlightExtension()] : []),
                        ]
                      }
                      style={{ borderTop: "1px solid #000000", borderBottom: "1px solid #000000" }}
                    />
                    {codeComments.length > 0 && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Line comments
                        </Typography>
                        <List dense disablePadding>
                          {codeComments
                            .sort((a, b) => a.line_number - b.line_number)
                            .map((c, idx) => (
                              <ListItem key={idx} disablePadding sx={{ py: 0.25 }}>
                                <ListItemText
                                  primary={c.code_comment}
                                  secondary={`Line ${c.line_number}`}
                                  primaryTypographyProps={{ variant: "body2" }}
                                  secondaryTypographyProps={{ variant: "caption" }}
                                />
                              </ListItem>
                            ))}
                        </List>
                      </Box>
                    )}
                  </>
                )}
              </Box>}
          </Paper>
        </Box>
      </Box>
      <BackgroundTasksPanel />
      <IdeHistoryPanel />
      <PlanProcessing
        open={proceedOpen}
        code={currentCode || ""}
        dash={dash}
        onClose={() => setProceedOpen(false)}
      />

      {showAutomationsPanel && (
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 1300,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            p: 1.5,
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 3,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" sx={{ px: 0.5, pb: 0.5 }}>
            Run automation
          </Typography>
          {providesCodeAutomations.map((automation) => (
            <Button
              key={automation.id}
              variant="contained"
              size="small"
              onClick={() =>
                actions.runAutomation({
                  automationId: automation.id,
                  selection: codeSelection!.trim(),
                  code: dash.repo_file_name ?? dash.name ?? "",
                })
              }
              sx={{
                bgcolor: "black",
                color: "white",
                "&:hover": { bgcolor: "grey.800" },
              }}
            >
              {automation.name}
            </Button>
          ))}
        </Box>
      )}
      { /*<ShowRelationships
        open={displayOpen}
        fileData={currentCode || ""}
        onClose={() => setDisplayOpen(false)}
      /> */}
    </Box>
  );
}
